let debug = false;

const setDebug = (value = true) => debug = value;

const getDebug = () => debug;

const debounce = (func, delay = 100) => {
    let timeoutId;
    return (...args) => {
        clearTimeout(timeoutId);
        timeoutId = setTimeout(() => queueMicrotask(() => func(...args)), delay);
    };
};

const idMap = new Map;

const id = (idName = "", idFormat = (id, idName) => `${idName}${idName ? "-" : ""}${id}`) => {
    const uniqueId = Math.random().toString(36).substring(2, 11);
    const id = idFormat(uniqueId, idName);
    idMap.set(idName, id);
    return id;
};

const getId = (idName = "") => idMap.get(idName);

const logMessage = (value, func) => {
    let strValue = JSON.stringify(value) ?? "undefined";
    if (strValue.length > 80) {
        strValue = strValue.slice(0, 77) + "...";
    }
    let strFunc = `${func}`;
    if (func.length > 80) {
        strFunc = strFunc.slice(0, 77) + "...";
    }
    return `${strValue} ➤ ${strFunc}`;
};

const isFunction = obj => obj instanceof Function;

const evalFactor = val => isFunction(val) ? val() : val;

const disposables = new WeakMap;

let currentDisposable = Symbol("global");

const shouldDispose = disposer => {
    const disposers = disposables.get(currentDisposable) ?? new Set;
    disposers.add(disposer);
    disposables.set(currentDisposable, disposers);
    return disposer;
};

const dispose = (obj, additionalInfo = "") => {
    const disposers = disposables.get(obj) ?? new Set;
    disposables.delete(obj);
    if (getDebug() === "verbose" && disposers.size) {
        console.log(`disposing ${obj.toString()} ${additionalInfo}`);
    }
    for (const disposer of [ ...disposers ]) {
        if (getDebug() === "verbose" && disposers.size) {
            console.log(`disposing ${obj.toString()} ${disposer}`);
        }
        disposer();
    }
};

const disposable = (obj, initializer) => {
    const save = currentDisposable;
    currentDisposable = obj;
    try {
        return initializer();
    } finally {
        currentDisposable = save;
    }
};

const targetSymbol = Symbol("target");

let nextTargetId = 1;

let getHook;

const setGetHook = hook => getHook = hook;

const getTargetId = p => p?.[targetSymbol];

const isSameTarget = (a, b) => (getTargetId(a) ?? a) === (getTargetId(b) ?? b);

const targetProxy = new Map;

const subscriberTargets = new Map;

const subscribe = (proxy, prop, callback) => {
    const targetId = getTargetId(proxy);
    if (!targetId) {
        throw new Error("Object must be observable.");
    }
    const subscriber = {
        prop: prop,
        callback: callback
    };
    if (!subscriberTargets.has(targetId)) {
        subscriberTargets.set(targetId, new Set);
    }
    subscriberTargets.get(targetId).add(subscriber);
    return shouldDispose(() => subscriberTargets.get(targetId)?.delete(subscriber));
};

const onChange = ({action: action, target: target, prop: prop, value: value, oldValue: oldValue}) => {
    if (subscriberTargets.has(getTargetId(target))) {
        const subscribers = [ ...subscriberTargets.get(getTargetId(target)) ].filter(s => prop == s.prop || !s.prop);
        if (getDebug() === "verbose") {
            console.log({
                count: subscribers.length,
                action: action,
                targetId: getTargetId(target),
                prop: prop,
                value: value,
                oldValue: oldValue
            });
        }
        for (const subscriber of subscribers) {
            queueMicrotask(() => subscriber.callback({
                action: action,
                prop: prop,
                value: value,
                oldValue: oldValue
            }));
        }
    }
};

const observable = target => {
    const createProxy = target => {
        let targetId = Reflect.get(target, targetSymbol, target);
        if (!targetId) {
            targetId = nextTargetId++;
            Reflect.defineProperty(target, targetSymbol, {
                value: targetId,
                writable: false,
                enumerable: false,
                configurable: false
            });
        }
        const current = new Proxy(target, {
            get: (target, prop, receiver) => {
                if (prop === targetSymbol) {
                    return targetId;
                }
                getHook?.({
                    action: "get",
                    proxy: current,
                    prop: prop
                });
                const value = target[prop];
                if (typeof value === "object" && value !== null) {
                    return targetProxy.get(getTargetId(value)) ?? createProxy(value);
                }
                return Reflect.get(target, prop, receiver);
            },
            set: (target, prop, value, receiver) => {
                const oldValue = Reflect.get(target, prop, receiver);
                if (isSameTarget(value, oldValue)) {
                    return true;
                }
                const result = Reflect.set(target, prop, value, receiver);
                onChange({
                    action: "set",
                    target: target,
                    prop: prop,
                    value: value,
                    oldValue: oldValue
                });
                return result;
            },
            deleteProperty: (target, prop) => {
                const oldValue = Reflect.get(target, prop);
                const result = Reflect.deleteProperty(target, prop);
                onChange({
                    action: "delete",
                    target: target,
                    prop: prop,
                    oldValue: oldValue
                });
                return result;
            }
        });
        targetProxy.set(getTargetId(target), current);
        return current;
    };
    return createProxy(target);
};

const reactTo = (func, delay = 0) => {
    const state = observable({
        computed: undefined
    });
    const events = [];
    const hook = event => {
        if (!events.some(e => getTargetId(e.proxy) === getTargetId(event.proxy) && e.prop === event.prop)) {
            events.push(event);
            subscribe(event.proxy, event.prop, compute);
        }
    };
    const reactToObj = Symbol("reactTo");
    shouldDispose(() => dispose(reactToObj, func));
    let compute = () => disposable(reactToObj, () => {
        try {
            setGetHook(hook);
            state.computed = evalFactor(func);
            setGetHook();
        } finally {
            setGetHook();
        }
    });
    compute();
    const withObj = Symbol("with");
    shouldDispose(() => dispose(withObj));
    const reactToApi = {
        with: (withCallback, {reset: reset} = {}) => {
            const wrapped = () => {
                if (reset) {
                    dispose(withObj, withCallback);
                }
                disposable(withObj, () => withCallback(state.computed));
            };
            disposable(reactToObj, () => {
                subscribe(state, "computed", debounce(({value: value}) => {
                    if (getDebug()) {
                        console.log(logMessage(value, func));
                    }
                    wrapped();
                }, delay));
                wrapped();
            });
            return reactToApi;
        },
        withReset: withCallback => reactToApi.with(withCallback, {
            reset: true
        })
    };
    return reactToApi;
};

const log = signal => reactTo(signal).with(v => console.log(logMessage(v, signal)));

const prop = (target, prop) => {
    const getTarget = () => evalFactor(target);
    const getProp = () => getTarget()?.[prop];
    Object.defineProperty(getProp, "value", {
        get: getProp,
        set: value => getTarget()[prop] = value
    });
    getProp.compute = funct => {
        reactTo(() => getTarget() && funct()).with(value => {
            if (getTarget()) {
                getTarget()[prop] = value;
            }
        });
        return getProp;
    };
    return getProp;
};

const on = (el, eventNames, handler, options) => {
    for (const eventName of eventNames.split(" ")) {
        el.addEventListener(eventName, handler, options);
        shouldDispose(() => el.removeEventListener(eventName, handler));
    }
};

let hideClass = "";

const hideWithClass = className => hideClass = className;

const createApi = (element = null) => {
    const api = {
        get el() {
            return element;
        },
        select: (selector, {optional: optional} = {}) => {
            if (!api.el) return createApi();
            const element = api.el.querySelector(selector);
            if (!element && !optional) {
                throw new Error(`Element not found for selector: "${selector}"`);
            }
            return createApi(element);
        },
        selectAll: (selector, connect) => {
            if (!api.el) return createApi();
            const elements = [ ...api.el.querySelectorAll(selector) ];
            elements.forEach(e => connect(createApi(e)));
            return api;
        },
        role: (roleName, options) => api.select(`[data-role='${roleName}']`, options),
        connect: connector => {
            if (!api.el) return createApi();
            connector(api);
            return api;
        },
        class: (className, hasClass) => {
            if (api.el) {
                const el = api.el;
                reactTo(hasClass).with(value => {
                    if (value) {
                        el.classList.add(className);
                    } else {
                        el.classList.remove(className);
                        if (el.classList.length === 0) {
                            el.removeAttribute("class");
                        }
                    }
                });
            }
            return api;
        },
        style: (styleName, styleValue) => {
            if (api.el) {
                reactTo(styleValue).with(value => api.el.style.setProperty(styleName, value ?? null));
            }
            return api;
        },
        show: condition => {
            if (hideClass) {
                return api.class(hideClass, () => !evalFactor(condition));
            } else {
                return api.style("display", () => evalFactor(condition) ? null : "none");
            }
        },
        attribute: (attributeName, attributeValue) => {
            if (api.el) {
                const el = api.el;
                reactTo(attributeValue).with(value => {
                    if (value == null) {
                        el.removeAttribute(attributeName);
                    } else {
                        el.setAttribute(attributeName, value);
                    }
                });
            }
            return api;
        },
        onAttribute: (attributeName, func) => {
            if (api.el) {
                const f = () => func(api.el?.getAttribute(attributeName) ?? null);
                const observer = new MutationObserver(f);
                observer.observe(api.el, {
                    attributes: true,
                    attributeFilter: [ attributeName ]
                });
                shouldDispose(() => observer.disconnect());
                f();
            }
            return api;
        },
        value: value => {
            if (api.el) {
                reactTo(value).with(value => api.el.value = value);
            }
            return api;
        },
        text: text => {
            if (api.el) {
                reactTo(text).with(text => api.el.textContent = text ?? null);
            }
            return api;
        },
        html: html => {
            if (api.el) {
                reactTo(html).with(html => api.el.innerHTML = html ?? "");
            }
            return api;
        },
        on: (eventNames, handler, options) => {
            if (api.el) {
                on(api.el, eventNames, handler, options);
            }
            return api;
        },
        model: prop => {
            if (api.el) {
                const el = api.el;
                const type = el.getAttribute("type")?.toUpperCase();
                if (api.el.tagName === "INPUT" && type === "CHECKBOX") {
                    reactTo(prop).with(value => el.checked = !!value);
                    api.on("change", () => prop.value = el.checked);
                } else if (api.el.tagName === "INPUT" && type === "RADIO") {
                    api.attribute("checked", () => prop.value == el.getAttribute("value") ? "" : null);
                    api.on("change", () => prop.value = el.value);
                } else if ((el.getAttribute("contenteditable") || "false").toLowerCase() !== "false") {
                    reactTo(prop).with(value => {
                        if (value != el.innerHTML) {
                            el.innerHTML = value?.toString() ?? "";
                        }
                    });
                    api.on("blur", () => prop.value = el.innerHTML);
                } else {
                    api.value(() => prop.value?.toString() ?? "");
                    api.on("change", () => prop.value = el.value);
                }
            }
            return api;
        },
        for: (items, itemConnector = () => {}) => {
            if (api.el) {
                const el = api.el;
                const template = [ ...api.el.children ].filter(c => c.tagName === "TEMPLATE")[0];
                if (!template) {
                    throw new Error(`"for" requires a template.`);
                }
                const targetToElement = new Map;
                const disposer = () => {
                    while (el.childElementCount - 1 > 0) {
                        dispose(el.lastElementChild, "for, dispose all");
                        el.lastElementChild?.remove();
                    }
                    targetToElement.clear();
                };
                reactTo(items).withReset(items => {
                    shouldDispose(disposer);
                    if (!items) {
                        disposer();
                        return;
                    }
                    const createChildComponent = item => {
                        const clone = template.content.cloneNode(true);
                        const div = document.createElement("div");
                        div.appendChild(clone);
                        const element = div.lastElementChild;
                        const targetId = getTargetId(item);
                        if (targetId) {
                            targetToElement.set(targetId, element);
                        }
                        disposable(element, () => {
                            itemConnector(createApi(element), item);
                        });
                        return element;
                    };
                    for (const item of items) {
                        el.appendChild(createChildComponent(item));
                    }
                    shouldDispose(subscribe(items, "", () => {
                        let i = 0;
                        for (const item of items) {
                            let newElement = targetToElement.get(getTargetId(item));
                            if (!newElement) {
                                newElement = createChildComponent(item);
                            }
                            if (i >= el.children.length - 1) {
                                el.appendChild(newElement);
                            } else {
                                const oldElement = el.children[i + 1];
                                if (oldElement != newElement) {
                                    oldElement.replaceWith(newElement);
                                }
                            }
                            i++;
                        }
                        while (el.childElementCount - 1 > i) {
                            el.lastElementChild?.remove();
                        }
                        const extras = [ ...targetToElement ].filter(e => ![ ...el.children ].includes(e[1]));
                        for (const e of extras) {
                            dispose(e[1], "for");
                            targetToElement.delete(e[0]);
                        }
                    }));
                });
            }
            return api;
        }
    };
    return api;
};

const {role: role, select: select, selectAll: selectAll} = createApi(document);

const follow = (func, delay = 0) => {
    const events = [];
    const hook = event => {
        if (!events.some(e => getTargetId(e.proxy) === getTargetId(event.proxy) && e.prop === event.prop)) {
            events.push(event);
            subscribe(event.proxy, event.prop, compute);
        }
    };
    const followObj = Symbol("follow");
    shouldDispose(() => dispose(followObj, func));
    let compute = debounce(() => disposable(followObj, () => {
        try {
            setGetHook(hook);
            func();
        } finally {
            setGetHook();
        }
    }), delay);
    compute();
};

export { disposable, dispose, follow, getId, hideWithClass, id, log, observable, on, prop, reactTo, role, select, selectAll, setDebug, shouldDispose };
//# sourceMappingURL=index.js.map
